import { Box, Button, Typography, useMediaQuery } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import DescriptionOutlinedIcon from '@evaneos/b2b-ui/icons/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@evaneos/b2b-ui/icons/FileDownloadOutlined';

import { MessageAttachment } from '../../../../../resources/discussion/types';
import { getFileTypeLabel } from '../../../../../resources/discussion/operations';

import { SharedByText } from './SharedByText';

type FileCardProps = {
    attachment: MessageAttachment;
    messageInfo?: { authorName?: string; authorId?: string; sendDate?: Date };
    onDownloadLinkClick: () => void;
};

/**
 * Accessible card pattern:
 * - Download is the primary action via an IconButton on the right
 * - On hover-capable devices: button hidden by default, revealed on hover/focus-within
 * - On touch devices: button always visible
 */
export const FileCard = ({ attachment, messageInfo, onDownloadLinkClick }: FileCardProps) => {
    const supportsHover = useMediaQuery('(hover: hover)');
    const { formatMessage } = useIntl();
    const typeLabel = getFileTypeLabel(attachment.type);

    return (
        <Box
            data-testid="attachment-document"
            data-spec-id="attachment-document"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                ...(supportsHover && {
                    '&:hover [data-file-download-button], &:focus-within [data-file-download-button]':
                        { opacity: 1 },
                }),
            }}
        >
            <Box
                sx={{
                    width: 50,
                    height: 50,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'background.main',
                    borderRadius: 1,
                }}
            >
                <DescriptionOutlinedIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }} noWrap>
                    {typeLabel} - {attachment.name}
                </Typography>
                <SharedByText messageInfo={messageInfo} />
            </Box>

            <Button
                data-file-download-button
                component="a"
                href={attachment.link}
                variant="outlined"
                aria-label={formatMessage(
                    { id: 'discussion.attachments_list.download_aria_label' },
                    { name: attachment.name },
                )}
                onClick={onDownloadLinkClick}
                sx={{
                    flexShrink: 0,
                    minWidth: 0,
                    p: 1,
                    opacity: supportsHover ? 0 : 1,
                    transition: 'opacity 150ms',
                }}
            >
                <FileDownloadOutlinedIcon />
            </Button>
        </Box>
    );
};
